import { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { UpNextWidget } from "@astrana/ui/modules";
import { GraphQLProvider } from "@astrana/ui/provider";

import { useModuleEnabled } from "../access/useFeatureAccess";
import { useAuthContext } from "../auth/AuthContext";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { useSessionEvents } from "../auth/useSessionEvents";
import { clinicalSummaryTitleOverride } from "../members/clinicalSummaryTitleOverride";
import { featureFlags } from "../config/featureFlags";
import { GraphQLClientGate } from "../lib/apollo/GraphQLClientGate";
import { useScheduleAccessGate } from "../schedule/useScheduleAccessGate";
import { useWorklistAccessGate } from "../worklist/useWorklistAccessGate";

// The care-management-gql endpoint the schedule resolver family (and this
// widget's own data) lives on — same singleton `@astrana/ui` configures via
// `<GraphQLProvider>`. Identical constant to PracticeSchedulePage.tsx.
const GRAPHQL_URL = import.meta.env.VITE_CARE_MANAGEMENT_GQL_ENDPOINT ?? "";

/**
 * Home-page host for @astrana/ui's native `UpNextWidget` — "current and next
 * patient on today's schedule, with clinical summary". Thin host, same shape
 * as `UrgentPatientsWidget.tsx`: module gating up top, an access gate below,
 * `null` whenever anything short of fully ready.
 *
 * GATING. Reuses the Practice Schedule entitlement (`useModuleEnabled(
 * "PracticeSchedule")`, the same module id `routeGates.tsx`'s
 * `PracticeScheduleGate` and `TabbedAppShell`'s nav-tab visibility check use)
 * rather than inventing a new flag — this widget surfaces the SAME schedule
 * data /schedule does, just condensed to "now and next".
 *
 * TRANSPORT. Wrapped in the exact `<GraphQLProvider>` / `<GraphQLClientGate>`
 * stack `PracticeSchedulePage.tsx` uses, against the same
 * `VITE_CARE_MANAGEMENT_GQL_ENDPOINT`, with the same `onAuthFailure` wiring
 * (`useSessionEvents().triggerExpiryWarning`) — see that file for why the
 * gate + identity-keyed provider exist (first-render Apollo-singleton race,
 * AM-5828 identity-not-token remount rule).
 *
 * NPI RESOLUTION — judgment call. `useScheduleAccessGate` (the gate
 * `/schedule` itself uses for its PP-bearer/session precondition) resolves
 * NO provider NPI at all: the schedule route deliberately asks the signed-in
 * user to pick a provider from a dropdown instead (see that hook's own
 * header comment — "no NPI entry, no IPA picker"). This widget renders with
 * NO props and no UI of its own, so it needs an NPI resolved WITHOUT asking
 * anyone. The only mechanism in this codebase that auto-resolves a signed-in
 * provider's NPI without user interaction is `useWorklistAccessGate`'s
 * auto-linked NPI (`accessGate.npi` — the exact field UrgentPatientsWidget
 * reads). Reusing it here does NOT couple this widget to the Priority
 * Worklist feature/data: the NPI comes from PP-API's `EntitledIpas` call,
 * which is a general provider-entitlement lookup, not priority-index-scoped.
 * Both gates must independently report "ready" before anything mounts, so a
 * provider-less user (e.g. an admin with no PP link, or no entitled NPI)
 * simply sees no widget — never a broken one.
 */
export default function HomeUpNextWidget() {
  const scheduleModuleEnabled = useModuleEnabled("PracticeSchedule");
  if (!scheduleModuleEnabled) return null;
  return <HomeUpNextWidgetGated />;
}

function HomeUpNextWidgetGated() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, accountLabel, selectedProvider } = useAuthContext();
  // AM-5828 — see MemberProfilePage/PracticeSchedulePage: identity, not the
  // token's value, is what may drop the Apollo cache.
  const apolloIdentityKey = `${selectedProvider ?? "none"}:${accountLabel ?? "anon"}`;
  const { triggerExpiryWarning } = useSessionEvents();

  const scheduleGate = useScheduleAccessGate();
  const worklistGate = useWorklistAccessGate();

  const { raw: currentUserRaw } = useCurrentUser();
  // Same resolver PracticeSchedulePage.tsx uses for its row cards' heading —
  // the two surfaces share one resolver so they can never disagree (see
  // clinicalSummaryTitleOverride.ts). `undefined` by default
  // (`personaClinicalSummaryTitle` off) lets @astrana/ui's own
  // "Clinical Summary" default win.
  const summaryTitle = clinicalSummaryTitleOverride(
    currentUserRaw?.currentPersona?.clinicalSummaryTemplate?.title,
    featureFlags.personaClinicalSummaryTitle,
  );

  // Same auth-failure signal MemberProfilePage/PracticeSchedulePage use, via
  // a stable ref so the provider's `onAuthFailure` prop identity doesn't
  // churn on every render.
  const authFailureRef = useRef<() => void>(() => {});
  authFailureRef.current = () => triggerExpiryWarning("auth-failure");
  const onAuthFailure = useRef(() => authFailureRef.current()).current;

  // Same route + nav-state shape PracticeSchedulePage's `openMember` uses —
  // `onOpenMember` here takes the same bare `memberId: string` PracticeSchedule
  // hands it, so the `astrana:`-prefixed PatientPriorityIndex nav shape
  // (UrgentPatientsWidget/HospitalEdEventsWidget) does not apply: those carry
  // extra priority-index-only fields (astranaId, ipaId, memberKeyId) this
  // widget's data never has.
  const onOpenMember = useCallback(
    (id: string) =>
      navigate(`/members/${id}`, {
        state: { from: location.pathname + location.search },
      }),
    [navigate, location.pathname, location.search],
  );

  // Same `from`-state nav idiom as `onOpenMember` above, pointed at the full
  // schedule page instead of a member profile — the library only knows to
  // call this, never the route itself.
  const onOpenSchedule = useCallback(
    () =>
      navigate("/schedule", {
        state: { from: location.pathname + location.search },
      }),
    [navigate, location.pathname, location.search],
  );

  // Same error sink PracticeSchedulePage wires its module's `onError` to —
  // this widget shares that file's GraphQL transport, so a load failure gets
  // the same toast rather than a silent console.error.
  const onError = useCallback(() => {
    toast.error("Couldn't load your next patient.");
  }, []);

  // Gate: no PP bearer, or no auto-resolved NPI → render nothing. A landing
  // page must not explain either precondition for a card the provider did
  // not ask for (mirrors UrgentPatientsWidget's own gate discipline).
  if (scheduleGate.status !== "ready") return null;
  if (worklistGate.status !== "ready" || !worklistGate.npi) return null;

  return (
    <GraphQLProvider
      clientUrl={GRAPHQL_URL}
      authToken={accessToken ?? undefined}
      ppBearer={scheduleGate.ppBearer}
      onAuthFailure={onAuthFailure}
      identityKey={apolloIdentityKey}
    >
      {/* AM-5828 — sign-in state, not the token value: rotation no longer
          needs a remount (the bearer is read live per request). */}
      <GraphQLClientGate key={accessToken ? "auth" : "anon"}>
        <UpNextWidget
          providerNpi={worklistGate.npi}
          onOpenMember={onOpenMember}
          onError={onError}
          summaryTitle={summaryTitle}
          onOpenSchedule={onOpenSchedule}
        />
      </GraphQLClientGate>
    </GraphQLProvider>
  );
}
